import type { Option } from './types';

/**
 * Option catalog for the Avatar Assistant.
 *
 * INVARIANT — fragments describe the CHARACTER CONCEPT only: WHO it is, its
 * role, its mood, and its signature details. They must NEVER describe HOW the
 * image is rendered (no "oil painting", "3D render", "pixel art", lighting,
 * camera, etc.). The qavatar-api owns art direction: because the TMA always
 * generates with `transparent: true`, the backend's TRANSPARENT_PROMPT_OVERRIDE
 * fully dictates the style (Clash Royale cartoon-painterly, one character,
 * magenta chroma background). Adding art-direction here would fight that and
 * degrade results.
 *
 * `promptFragment` conventions (so composePrompt reads as a natural sentence):
 *   - archetype → article-less noun phrase ("battle-hardened warrior in heavy armor")
 *   - vibe      → adjective phrase ("fierce", "dark and menacing")
 *   - accent    → noun phrase ("glowing eyes", "a flowing cape")
 *
 * `labelKey` resolves in the `avatarAssistant` i18n namespace.
 */

/** Max number of accents a user can stack (keeps the composed prompt < 500). */
export const MAX_ACCENTS = 3;

export const ARCHETYPES: readonly Option[] = [
  { id: 'warrior', labelKey: 'archetypes.warrior', icon: '⚔️', promptFragment: 'battle-hardened warrior in heavy armor' },
  { id: 'mage', labelKey: 'archetypes.mage', icon: '🔮', promptFragment: 'mystical mage channeling arcane energy' },
  { id: 'beast', labelKey: 'archetypes.beast', icon: '🐺', promptFragment: 'ferocious anthropomorphic beast creature' },
  { id: 'mech', labelKey: 'archetypes.mech', icon: '🤖', promptFragment: 'sleek armored combat mech' },
  { id: 'ninja', labelKey: 'archetypes.ninja', icon: '🥷', promptFragment: 'stealthy hooded ninja assassin' },
  { id: 'archer', labelKey: 'archetypes.archer', icon: '🏹', promptFragment: 'sharp-eyed ranger archer holding a longbow' },
  { id: 'royal', labelKey: 'archetypes.royal', icon: '👑', promptFragment: 'regal monarch in ornate royal regalia' },
  { id: 'monster', labelKey: 'archetypes.monster', icon: '👹', promptFragment: 'towering monstrous creature' },
  { id: 'pilot', labelKey: 'archetypes.pilot', icon: '🚀', promptFragment: 'daring ace pilot in a flight suit' },
  { id: 'mascot', labelKey: 'archetypes.mascot', icon: '🎮', promptFragment: 'playful esports team mascot' },
] as const;

export const VIBES: readonly Option[] = [
  { id: 'fierce', labelKey: 'vibes.fierce', icon: '😤', promptFragment: 'fierce' },
  { id: 'heroic', labelKey: 'vibes.heroic', icon: '🛡️', promptFragment: 'heroic' },
  { id: 'dark', labelKey: 'vibes.dark', icon: '🌑', promptFragment: 'dark and menacing' },
  { id: 'playful', labelKey: 'vibes.playful', icon: '😎', promptFragment: 'playful' },
  { id: 'mysterious', labelKey: 'vibes.mysterious', icon: '🌫️', promptFragment: 'mysterious' },
  { id: 'regal', labelKey: 'vibes.regal', icon: '💎', promptFragment: 'regal' },
  { id: 'chaotic', labelKey: 'vibes.chaotic', icon: '🔥', promptFragment: 'wild and chaotic' },
  { id: 'cute', labelKey: 'vibes.cute', icon: '🐾', promptFragment: 'cute' },
] as const;

export const ACCENTS: readonly Option[] = [
  { id: 'glowingEyes', labelKey: 'accents.glowingEyes', icon: '✨', promptFragment: 'glowing eyes' },
  { id: 'neon', labelKey: 'accents.neon', icon: '💡', promptFragment: 'neon accents' },
  { id: 'goldArmor', labelKey: 'accents.goldArmor', icon: '🥇', promptFragment: 'golden armor details' },
  { id: 'flames', labelKey: 'accents.flames', icon: '🔥', promptFragment: 'flames' },
  { id: 'ice', labelKey: 'accents.ice', icon: '❄️', promptFragment: 'frost and ice' },
  { id: 'aura', labelKey: 'accents.aura', icon: '🌀', promptFragment: 'a radiant energy aura' },
  { id: 'visor', labelKey: 'accents.visor', icon: '🕶️', promptFragment: 'a cybernetic visor' },
  { id: 'crown', labelKey: 'accents.crown', icon: '👑', promptFragment: 'a jeweled crown' },
  { id: 'warPaint', labelKey: 'accents.warPaint', icon: '🎨', promptFragment: 'tribal war paint' },
  { id: 'lightning', labelKey: 'accents.lightning', icon: '⚡', promptFragment: 'crackling lightning' },
  { id: 'smoke', labelKey: 'accents.smoke', icon: '💨', promptFragment: 'swirling smoke' },
  { id: 'cape', labelKey: 'accents.cape', icon: '🧥', promptFragment: 'a flowing cape' },
] as const;

/** Lookup helpers used by composePrompt + the wizard. */
export const CATALOG = { archetypes: ARCHETYPES, vibes: VIBES, accents: ACCENTS } as const;
